<template>
  <div
    class="ls-detail"
    @click.self="$emit('close')"
  >
    <div
      ref="dialog"
      class="ls-detail__panel"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <button
        ref="close"
        type="button"
        class="ls-detail__close"
        data-test="ls-detail-close"
        aria-label="Close"
        @click="$emit('close')"
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
          focusable="false"
        ><path
          d="M6 6l12 12M18 6 6 18"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        /></svg>
      </button>
      <div
        class="ls-detail__gallery"
        :class="{ 'is-empty': !images.length }"
      >
        <img
          v-for="(url, i) in images"
          :key="url"
          :src="url"
          :alt="i === 0 ? name : `${name}, view ${i + 1}`"
          :loading="i === 0 ? 'eager' : 'lazy'"
          decoding="async"
        >
        <span
          v-if="!images.length"
          class="ls-detail__tile"
        >{{ name }}</span>
      </div>
      <div class="ls-detail__info">
        <h2
          :id="titleId"
          class="ls-detail__name"
        >{{ name }}</h2>
        <p
          v-if="product.styleCode"
          class="ls-detail__code"
        >Style {{ product.styleCode }}</p>
        <p
          v-if="line"
          class="ls-detail__line"
        >{{ line }}</p>
        <p
          v-if="prices.length"
          class="ls-detail__prices"
        >
          <span
            v-for="price in prices"
            :key="price.kind"
          ><span class="ls-detail__label">{{ price.label }}</span> {{ price.text }}</span>
        </p>
        <dl class="ls-detail__facts">
          <div v-if="colours.length">
            <dt>Colours</dt>
            <dd>{{ colours.join(', ') }}</dd>
          </div>
          <div v-if="sizes.length">
            <dt>Sizes</dt>
            <dd>{{ sizes.join(', ') }}</dd>
          </div>
          <div v-if="moq">
            <dt>Minimum order</dt>
            <dd>{{ moq }} per style</dd>
          </div>
          <div v-if="product.descriptionMaterials">
            <dt>Materials</dt>
            <dd>{{ product.descriptionMaterials }}</dd>
          </div>
          <div v-if="product.descriptionLong">
            <dt>Description</dt>
            <dd>{{ product.descriptionLong }}</dd>
          </div>
        </dl>
        <button
          type="button"
          class="ls-detail__heart"
          data-test="ls-detail-heart"
          :aria-pressed="shortlisted ? 'true' : 'false'"
          @click="$emit('toggle', product._id)"
        >{{ shortlisted ? 'On your shortlist' : 'Add to shortlist' }}</button>
        <ls-product-feedback
          v-if="feedbackOn"
          :brand-name="brandName"
          :interested="interested"
          :comment="comment"
          :status="saveStatus"
          :limit="commentLimit"
          @interest="$emit('interest', product._id)"
          @comment="text => $emit('comment', product._id, text)"
          @commit="$emit('commit')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { productPrices } from './priceFormat'
import { productColours, productImages } from './lineSheetModel'
import { trapTab } from './focusTrap'
import LsProductFeedback from './LsProductFeedback.vue'

export default {
  name: 'LsProductDetail',
  components: { LsProductFeedback },
  props: {
    product: { type: Object, required: true },
    line: { type: String, default: '' },
    priceVisibility: { type: Object, default: undefined },
    shortlisted: { type: Boolean, default: false },
    // KP-4572: interest and a note to the brand, while feedback is available.
    feedbackOn: { type: Boolean, default: false },
    brandName: { type: String, default: 'the brand' },
    interested: { type: Boolean, default: false },
    comment: { type: String, default: '' },
    saveStatus: { type: String, default: 'idle' },
    commentLimit: { type: Number, default: 1000 },
  },
  data() {
    return { returnFocus: null, previousOverflow: '' }
  },
  computed: {
    titleId() {
      return `ls-detail-${this.product._id}`
    },
    name() {
      return this.product.name || 'Untitled style'
    },
    images() {
      return productImages(this.product)
    },
    prices() {
      return productPrices(this.product, this.priceVisibility)
    },
    colours() {
      return productColours(this.product).map(c => c.name)
    },
    sizes() {
      const items = Array.isArray(this.product.items) ? this.product.items : []
      return [...new Set(items.map(i => i && i.size).filter(Boolean))]
    },
    moq() {
      const n = Number(this.product.minQtyPerStyle)
      return Number.isFinite(n) && n > 0 ? n : 0
    },
  },
  mounted() {
    this.returnFocus = document.activeElement
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    this.$nextTick(() => this.$refs.close && this.$refs.close.focus())
  },
  beforeDestroy() {
    document.body.style.overflow = this.previousOverflow
    if (this.returnFocus && typeof this.returnFocus.focus === 'function') this.returnFocus.focus()
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('close')
        return
      }
      if (event.key === 'Tab') trapTab(this.$refs.dialog, event)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

.ls-detail {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  animation: ls-detail-in 0.2s $ls-ease both;

  @include ls-up($ls-lg) {
    align-items: center;
    padding: 32px;
  }

  &__panel {
    position: relative;
    display: grid;
    width: 100%;
    max-height: 92svh;
    overflow-y: auto;
    overscroll-behavior: contain;
    background: var(--ls-ground);
    color: var(--ls-ink);
    border-radius: var(--ls-radius) var(--ls-radius) 0 0;
    outline: none;

    @include ls-up($ls-lg) {
      grid-template-columns: minmax(0, 6fr) minmax(0, 5fr);
      max-width: 1080px;
      max-height: min(86svh, 820px);
      border-radius: var(--ls-radius);
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.24);
    }
  }

  &__close {
    position: sticky;
    top: 8px;
    z-index: 2;
    justify-self: end;
    grid-column: 1 / -1;
    grid-row: 1;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    margin: 8px 8px -52px 0;
    border: 0;
    border-radius: 999px;
    background: var(--ls-ground);
    color: var(--ls-ink);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
    cursor: pointer;
    @include ls-focus;
  }

  // Phones: images scroll sideways with snap. Desktop: they stack.
  &__gallery {
    grid-row: 1;
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    background: var(--ls-surface);

    img {
      flex: 0 0 100%;
      width: 100%;
      aspect-ratio: 4 / 5;
      object-fit: cover;
      scroll-snap-align: start;
    }

    @include ls-up($ls-lg) {
      grid-column: 1;
      flex-direction: column;
      gap: 4px;
      overflow: visible;
    }

    &.is-empty {
      aspect-ratio: 4 / 5;
      align-items: center;
      justify-content: center;
    }
  }

  &__tile {
    padding: 16%;
    @include ls-heading;
    font-size: 1.75rem;
    text-align: center;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px $ls-gutter 40px;

    @include ls-up($ls-lg) {
      grid-column: 2;
      grid-row: 1;
      align-self: start;
      position: sticky;
      top: 0;
      padding: 56px 40px 40px;
    }
  }

  &__name {
    @include ls-heading;
    margin: 0;
    font-size: clamp(1.75rem, 3vw, 2.25rem);
  }

  &__code {
    margin: 0;
    @include ls-label;
    color: var(--ls-muted);
    font-weight: 500;
  }

  &__line {
    margin: 0;
    color: var(--ls-ink);
    font: 400 1.0625rem/1.6 var(--ls-body-font);
  }

  &__prices {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 20px;
    margin: 0;
    font: 500 1.0625rem/1.4 var(--ls-body-font);
    font-variant-numeric: tabular-nums;
  }

  &__label {
    color: var(--ls-muted);
    font-weight: 400;
  }

  &__facts {
    margin: 8px 0 0;
    border-top: 1px solid var(--ls-line);

    div {
      padding: 12px 0;
      border-bottom: 1px solid var(--ls-line);
    }

    dt {
      @include ls-label;
      color: var(--ls-muted);
    }

    dd {
      margin: 6px 0 0;
      font: 400 0.9375rem/1.6 var(--ls-body-font);
      white-space: pre-line;
    }
  }

  &__heart {
    min-height: 48px;
    margin-top: 8px;
    border: 1px solid var(--ls-primary);
    border-radius: var(--ls-radius);
    background: var(--ls-primary);
    color: var(--ls-on-primary);
    font: 600 0.9375rem/1 var(--ls-body-font);
    cursor: pointer;
    @include ls-focus;

    &[aria-pressed='true'] {
      background: transparent;
      color: var(--ls-ink);
      border-color: var(--ls-ink);
    }
  }
}

@keyframes ls-detail-in {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ls-detail {
    animation: none;
  }
}
</style>
